import { StyleSheet, Text, View } from "react-native";
import type { VolumeLandmarkBand, VolumeLandmarkZone } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { makeMeterTrackStyle } from "./meterSharedStyles";

// <LandmarkBar> (AP-9) — the week's hard sets against the EXTERNAL MEV/MAV/MRV
// band. A sibling of <TargetBar>, deliberately not an extension of it.
//
// ⛔ Why a sibling and not a prop on TargetBar. The two bars answer different
// questions against different references. TargetBar scores the actual against a
// goal THE ATHLETE wrote, in three zones (under / in-band / over), and draws ONE
// caret at that goal. This one scores the same actual against a PUBLISHED
// reference nobody in the app authored, in four zones, and draws THREE
// boundaries at once. `targetBarGeom` returns a single `markerPct`: an array of
// boundaries does not fit its shape, and forcing the 4 zones into `TargetZone`
// is the collapse volumeLandmarks.ts:112-115 refuses by name ("would lose the
// distinction between building and at the productive plateau, which is the
// whole point of the metre"). Contract: §16.4bis.
//
// What IS reused, and on purpose: the drawing idiom. Absolutely-positioned
// Views at percentage offsets inside a clipping track — RN-portable, no SVG,
// identical to TargetBar so the two bars sit in one list without reading as
// two different widgets.
//
// The DESIGN is Antenore's, chosen 2026-09-15 off an HTML canvas ("variante C
// con i colori"): the boundaries are drawn as ticks AND the fill carries the
// zone colour. The ticks are not decoration — `mev-mav` and `mav-mrv` share the
// same green (four zones, three state tokens), so without them the two
// productive zones would be indistinguishable at a glance. The tick you just
// passed is emphasised; that is what tells them apart.

// Zone → the fill colour. ⛔ NOT `zoneFillColor` from TargetBar: that one is
// typed on `TargetZone` and its comment says it is exported so a second surface
// painting THE SAME zones can't drift from the bar. These are not the same
// zones, so reusing it would be the very drift that comment prevents (§16.4bis).
// Four zones onto three tokens, Antenore's mapping: the two training zones
// share `complete`; `above-mrv` is `warn` gold, ⛔ never `danger` red — the same
// rule TargetBar states at its own `over`.
export const landmarkZoneFill = (
  theme: UiTheme,
  zone: VolumeLandmarkZone,
): string =>
  zone === "mev-mav" || zone === "mav-mrv"
    ? theme.palette.complete
    : zone === "above-mrv"
      ? theme.palette.warn
      : theme.palette.ink3;

// A glyph per zone, so the state is never carried by colour alone — and, here,
// so the two zones that SHARE the green are still told apart by a reader who
// sees no colour at all.
//
// 🪤 The first pair was `◐` (half disc) and `●` (full disc), and the a11y audit
// caught it as EXACTLY the mistake this repo already made and fixed once:
// `TargetBar.tsx:34-38` rejected a hollow ring against a filled dot because "a
// hollow ring blurs into the filled dot for a reduced-vision reader". Two discs
// differing only in how much of them is filled is the same failure wearing a
// different shape. `◐` also has thinner font coverage and can land as tofu.
// ⇒ The four glyphs now differ by SILHOUETTE, not by fill: arrow down, square,
// tick, arrow up. `✓` is borrowed on purpose from `ZONE_GLYPH`'s `in-band` — on
// both bars it says the same thing to a reader, "this one is where it should be".
export const LANDMARK_ZONE_GLYPH: Record<VolumeLandmarkZone, string> = {
  "below-mev": "↓",
  "mev-mav": "▪",
  "mav-mrv": "✓",
  "above-mrv": "↑",
};

// The three muscles with no published band (adductors, abductors, obliques).
// ⛔ Their row shows no bar, no ticks and no colour: unknown is a third state,
// never a borrowed number. Same empty-set mark TargetBar uses for no-coverage —
// the meaning is the same shape of absence.
export const NO_BAND_GLYPH = "∅";

export const noBandWord = (
  t: (k: string, opts?: Record<string, unknown>) => string,
): string =>
  t("progress.volumeLandmark.zone.noBand", { defaultValue: "no published band" });

export const landmarkZoneWord = (
  t: (k: string, opts?: Record<string, unknown>) => string,
  zone: VolumeLandmarkZone,
): string =>
  zone === "below-mev"
    ? t("progress.volumeLandmark.zone.belowMev", { defaultValue: "below MEV" })
    : zone === "mev-mav"
      ? t("progress.volumeLandmark.zone.mevMav", { defaultValue: "MEV–MAV" })
      : zone === "mav-mrv"
        ? t("progress.volumeLandmark.zone.mavMrv", { defaultValue: "MAV–MRV" })
        : t("progress.volumeLandmark.zone.aboveMrv", {
            defaultValue: "above MRV",
          });

export type LandmarkTick = {
  /** Where the boundary sits on the track, 0-100. */
  pct: number;
  /** MEV / MAV / MRV — the caller renders it as the tick's label. */
  label: "MEV" | "MAV" | "MRV";
  /** The boundary this week's volume has most recently passed. Exactly one
   *  tick is lit, or none when the volume is below MEV. */
  lit: boolean;
  /** Whether to print this tick's WORD. The tick line itself is always drawn;
   *  only the label can be dropped. See LABEL_MIN_GAP_PCT. */
  labelled: boolean;
};

// Two boundaries can sit almost on top of each other. 🪤 The first version of
// this comment said "chest is MEV 10, MAV low 12": those numbers DO NOT EXIST.
// `volumeLandmarks.ts:84` has `pectoralis: mev 8, mav [12, 20]`, a 16% gap that
// never collides. The real case in the shipped data is `erectors`
// (`volumeLandmarks.ts:82`, mev 6 / mav low 8) — ~11% apart, and the ONLY one of
// the 16 bands that ever drops a label. A comment citing numbers nobody can find
// reads as authoritative and is worse than no comment: it survived a whole
// review round before being measured. Below this gap the later label is dropped
// rather than overprinted. ⛔ A lit tick NEVER loses its label: it is
// the one that tells `mev-mav` from `mav-mrv`, which is the whole reason the
// ticks exist (§16.4bis). Measured in percent because geometry here is
// percent-based: at the 12pt floor "MEV" sets at ~30dp, and 16% of a 300dp
// row is ~48dp — enough for the word plus air on both sides.
const LABEL_MIN_GAP_PCT = 16;

export type LandmarkBarGeom = {
  fillPct: number;
  ticks: LandmarkTick[];
};

const pct = (n: number): number => Math.max(0, Math.min(100, n));

/** Pure — projects a week's hard sets and a band onto the track. Exported for
 *  tests; the component is the only render caller.
 *
 *  The scale runs past MRV so an above-MRV week still lands ON the track with
 *  its ceiling tick visible, instead of pinning at 100% with the MRV tick under
 *  the fill's right edge. Mirrors TargetBar's 12% headroom for the same reason.
 *
 *  ⛔ `mav` is a RANGE [low, high] but only its LOW edge is a boundary between
 *  zones (`landmarkZoneFor` switches at `mav[0]`). Drawing `mav[1]` too would
 *  put a tick where no zone changes, which is a line that means nothing. */
export function landmarkBarGeom(
  weeklySets: number,
  band: VolumeLandmarkBand,
  zone: VolumeLandmarkZone,
): LandmarkBarGeom {
  // ⛔ NaN first, and not as defensive noise: `NaN <= 0` is false, so a NaN would
  // sail past the zero branch below into `width: "NaN%"` on a live row. The same
  // hole exists in `targetBarGeom` and was copied here before the review caught
  // it; closing it there is that bar's own fix, not this one's.
  const sets = Number.isFinite(weeklySets) ? weeklySets : 0;
  const scale = Math.max(sets, band.mrv) * 1.12 || 1;
  const at = (v: number): number => pct((v / scale) * 100);
  const raw: Array<Omit<LandmarkTick, "labelled">> = [
    { pct: at(band.mev), label: "MEV", lit: zone === "mev-mav" },
    { pct: at(band.mav[0]), label: "MAV", lit: zone === "mav-mrv" },
    { pct: at(band.mrv), label: "MRV", lit: zone === "above-mrv" },
  ];
  // Two passes, because the lit tick has priority and can sit anywhere. A
  // single left-to-right pass that simply forced the lit label through would
  // print it ON TOP of a neighbour already kept — the exact overlap the gap
  // exists to prevent. So: the lit tick claims its place first, then the others
  // are kept only where they clear EVERY place already claimed.
  const kept: number[] = raw.filter((tk) => tk.lit).map((tk) => tk.pct);
  const clears = (x: number): boolean =>
    kept.every((k) => Math.abs(x - k) >= LABEL_MIN_GAP_PCT);
  const ticks: LandmarkTick[] = raw.map((tk) => {
    if (tk.lit) return { ...tk, labelled: true };
    const labelled = clears(tk.pct);
    if (labelled) kept.push(tk.pct);
    return { ...tk, labelled };
  });
  return {
    fillPct: sets <= 0 ? 0 : Math.max(3, at(sets)),
    ticks,
  };
}

export type LandmarkBarProps = {
  theme: UiTheme;
  /** This week's hard sets for the muscle, fractional credit included. */
  weeklySets: number;
  /** The published band. ⛔ Absent → the caller shows no bar at all (the three
   *  unbanded muscles), so this component is never asked to draw an unknown. */
  band: VolumeLandmarkBand;
  zone: VolumeLandmarkZone;
  /** PG-64 fetta 2: a «you are here» dot at the end of the fill. */
  here?: boolean;
};

export function LandmarkBar({
  theme,
  weeklySets,
  band,
  zone,
  here = false,
}: LandmarkBarProps) {
  const styles = makeStyles(theme);
  const g = landmarkBarGeom(weeklySets, band, zone);
  return (
    // Two layers, like TargetBar and for the same reason: the ticks overflow
    // the track vertically, so only the inner track clips.
    <View style={styles.barWrap}>
      <View style={styles.track}>
        <View
          style={[
            styles.fill,
            { backgroundColor: landmarkZoneFill(theme, zone) },
            { width: `${g.fillPct}%` },
          ]}
        />
      </View>
      {g.ticks.map((tk) => (
        <View
          key={tk.label}
          style={[
            styles.tick,
            tk.lit ? styles.tickLit : null,
            { left: `${tk.pct}%` },
          ]}
        />
      ))}
      {g.ticks
        .filter((tk) => tk.labelled)
        .map((tk) => (
          <Text
            key={`${tk.label}-label`}
            style={[styles.tickLabel, tk.lit ? styles.tickLabelLit : null, { left: `${tk.pct}%` }]}
          >
            {tk.label}
          </Text>
        ))}
      {here ? <View style={[styles.here, { left: `${g.fillPct}%` }]} /> : null}
    </View>
  );
}

// Where the boundary names sit, measured from the top of the wrap, and how
// tall one line of them is. DS-88 — both numbers live here because `barWrap`
// has to RESERVE the label's height: the labels are absolutely positioned, and
// in RN an absolute child does not grow its parent, so the wrap used to be as
// tall as the 10px track alone and the next row in the flow (the "avg …" trend
// line) drew straight over the words.
const TICK_LABEL_TOP = 15;
const TICK_LABEL_LINE = 1.3;
// The «you are here» dot, centred on the track (meterSharedStyles: 10 tall).
const HERE_SIZE = 12;
const TRACK_HEIGHT = 10;

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: ty } = theme;
  const tickLabelLineHeight = Math.round(ty.sizes.sm * TICK_LABEL_LINE);
  return StyleSheet.create({
    barWrap: {
      marginTop: 10,
      position: "relative",
      // 10 = the track's own height, the only thing in the flow here.
      paddingBottom: TICK_LABEL_TOP + tickLabelLineHeight - 10,
    },
    // Chrome condiviso con TargetBar — una sola definizione (meterSharedStyles).
    ...makeMeterTrackStyle(theme),
    // A boundary tick: a thin vertical line crossing the whole track and poking
    // out both ends, so it stays readable over the fill AND over bare track.
    // ⛔ Deliberately NOT the caret+stem of TargetBar's target marker: that
    // silhouette means "the goal you set", and a reader who knows the other bar
    // must not read a published boundary as their own goal.
    // 🪤 Misurato sul banco visivo il 15/09: a `p.border` la tacca NON SI VEDE
    // sul fondo scuro — restavano le tre etichette sotto una barra senza linee,
    // cioè parole che indicano un punto invisibile. `ink3` è il grado che il
    // repo usa per un segno informativo quieto ma presente (lo stesso di
    // `zoneFillColor` per `under`), e regge su entrambi i temi.
    tick: {
      position: "absolute",
      top: -3,
      height: 16,
      width: 1,
      backgroundColor: p.ink3,
      transform: [{ translateX: -0.5 }],
    },
    // The boundary just passed — wider and at full ink, because this is the
    // tick that tells `mev-mav` from `mav-mrv` when both fills are the same
    // green. Weight and width, not hue: the distinction has to survive a reader
    // who sees no colour.
    tickLit: {
      width: 2,
      backgroundColor: p.ink2,
      transform: [{ translateX: -1 }],
    },
    // The boundary's name, under its tick. translateX(-50%) is not available in
    // RN, so the label is centred by a fixed negative offset on a fixed width —
    // the three words are all three characters, so one width fits them all.
    // 🪤 Era 9.5px, sotto il floor DS-16 di 12pt che questa stessa card enforce
    // per le etichette (MuscleCoverageCompactSection: "sm (not xs): the DS-16 12pt
    // legend/label floor"). ⛔ E questi tre sono i VALORI DEI CONFINI, cioè la
    // ragione per cui la barra esiste: erano il testo più piccolo della card.
    // Allargandoli si scontrano più spesso, quindi LABEL_MIN_GAP_PCT sale con loro.
    tickLabel: {
      position: "absolute",
      top: TICK_LABEL_TOP,
      lineHeight: tickLabelLineHeight,
      width: 36,
      marginLeft: -18,
      textAlign: "center",
      color: p.ink3,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    tickLabelLit: { color: p.ink2, fontWeight: "700" },
    // Full ink ringed in the surface: it reads over the fill and over the
    // bare track alike.
    here: {
      position: "absolute",
      top: (TRACK_HEIGHT - HERE_SIZE) / 2,
      width: HERE_SIZE,
      height: HERE_SIZE,
      marginLeft: -HERE_SIZE / 2,
      borderRadius: HERE_SIZE / 2,
      backgroundColor: p.ink,
      borderWidth: 2,
      borderColor: p.surface,
    },
  });
}
